import { api } from '@stageplottter/core';
import type { ApiClient, RequestOptions, Schema } from '../../api/client';
import { ApiError, NetworkError, listOf } from '../../api/client';
import { getApi } from '../../api/endpoints';

const MarkReadResult: Schema<{ updated: number }> = {
  safeParse(data) {
    const v = data as { updated?: unknown } | null;
    return v && typeof v === 'object' && Number.isInteger(v.updated)
      ? { success: true, data: { updated: v.updated as number } }
      : { success: false, error: { message: 'expected { updated: int }' } };
  },
};

const LockResult: Schema<{ id: string; lockedAt: string | null }> = {
  safeParse(data) {
    const v = data as { id?: unknown; lockedAt?: unknown } | null;
    return v &&
      typeof v === 'object' &&
      typeof v.id === 'string' &&
      (v.lockedAt === null || typeof v.lockedAt === 'string')
      ? { success: true, data: { id: v.id, lockedAt: v.lockedAt } }
      : { success: false, error: { message: 'expected { id, lockedAt }' } };
  },
};

export type CommentAnchorInput = api.CommentAnchor;
export const ATTACHMENT_MIMES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
] as const;
export type AttachmentMime = (typeof ATTACHMENT_MIMES)[number];

export function isAttachmentMime(m: string): m is AttachmentMime {
  return (ATTACHMENT_MIMES as readonly string[]).includes(m);
}

/**
 * Sharing / collaboration endpoints (links, guest share, comments, notifications, snapshots,
 * members, admin). In guest mode a 401 never redirects to the member login.
 */
export function createSharingApi(client: ApiClient, opts: { guest?: boolean } = {}) {
  const enc = encodeURIComponent;
  const base: Pick<RequestOptions<unknown>, 'redirectOn401'> = opts.guest
    ? { redirectOn401: false }
    : {};
  const req = <T>(method: string, path: string, o: RequestOptions<T> = {}) =>
    client.request<T>(method, path, { ...base, ...o } as RequestOptions<T>);

  return {
    guest: opts.guest === true,
    // links (admins)
    listLinks: (boardId: string) =>
      req('GET', `/boards/${enc(boardId)}/links`, { schema: listOf(api.Link) }),
    createLink: (
      boardId: string,
      body: {
        name: string;
        access: 'view' | 'comment' | 'edit';
        expiresAt?: string;
        layerKeys?: string[] | null;
      },
    ) => req('POST', `/boards/${enc(boardId)}/links`, { body, schema: api.Link }),
    updateLink: (
      lid: string,
      body: { name?: string; action?: 'revoke' | 'extend' | 'rotate'; expiresAt?: string },
    ) => req('PATCH', `/links/${enc(lid)}`, { body, schema: api.Link }),
    deleteLink: (lid: string) => req<void>('DELETE', `/links/${enc(lid)}`),

    // discussion layers (U4): comment/edit links, readable by every board member
    listOverlays: (boardId: string) =>
      req('GET', `/boards/${enc(boardId)}/overlays`, { schema: listOf(api.OverlayInfo) }),
    mentionable: (token: string) =>
      req('GET', `/share/${enc(token)}/mentionable`, {
        schema: listOf(api.MentionCandidate),
        redirectOn401: false,
      }),

    // guest share
    shareInfo: (token: string) =>
      req('GET', `/share/${enc(token)}`, { schema: api.ShareInfo, redirectOn401: false }),
    /** Persons already registered on this link ("I am …", decision C1 revised). */
    listPersons: (token: string) =>
      req('GET', `/share/${enc(token)}/persons`, {
        schema: listOf(api.GuestPerson),
        redirectOn401: false,
      }),
    createGuestSession: (token: string, body: api.CreateGuestSession) =>
      req('POST', `/share/${enc(token)}/session`, {
        body,
        schema: api.ShareInfo,
        redirectOn401: false,
      }),
    shareDocument: (token: string) =>
      req('GET', `/share/${enc(token)}/document`, {
        schema: api.DocumentResponse,
        redirectOn401: false,
      }),
    // releases for guests (U80, OIL-163)
    guestReleases: (token: string) =>
      req('GET', `/share/${enc(token)}/releases`, {
        schema: api.GuestReleases,
        redirectOn401: false,
      }),
    guestRelease: (token: string, sid: string) =>
      req('GET', `/share/${enc(token)}/releases/${enc(sid)}`, {
        schema: api.GuestReleaseDetail,
        redirectOn401: false,
      }),
    guestSignOff: (
      token: string,
      sid: string,
      body: { name: string; role: string; comment?: string; signature?: string },
    ) =>
      req('POST', `/share/${enc(token)}/releases/${enc(sid)}/signoffs`, {
        body,
        schema: api.GuestRelease,
        redirectOn401: false,
      }),
    requestExtension: (token: string, message?: string) =>
      req<void>('POST', `/share/${enc(token)}/extension-request`, {
        body: message === undefined ? {} : { message },
        redirectOn401: false,
      }),

    // comments
    listComments: (boardId: string) =>
      req('GET', `/boards/${enc(boardId)}/comments`, { schema: listOf(api.Comment) }),
    createComment: (
      boardId: string,
      body: {
        body: string;
        anchor: api.CommentAnchor;
        parentId?: string;
        attachmentIds?: string[];
      },
    ) => req('POST', `/boards/${enc(boardId)}/comments`, { body, schema: api.Comment }),
    updateComment: (cid: string, body: string) =>
      req('PATCH', `/comments/${enc(cid)}`, { body: { body }, schema: api.Comment }),
    deleteComment: (cid: string) => req<void>('DELETE', `/comments/${enc(cid)}`),
    resolveComment: (cid: string) =>
      req('POST', `/comments/${enc(cid)}/resolve`, { schema: api.Comment }),
    reopenComment: (cid: string) =>
      req('POST', `/comments/${enc(cid)}/reopen`, { schema: api.Comment }),
    createUpload: (boardId: string, body: { name: string; mime: AttachmentMime; size: number }) =>
      req('POST', `/boards/${enc(boardId)}/files`, { body, schema: api.UploadResponse }),

    // notifications
    listNotifications: () =>
      req('GET', '/notifications', {
        schema: listOf(api.Notification),
        query: { limit: 50 },
        redirectOn401: false,
      }),
    markNotificationsRead: (body: { ids?: string[]; all?: boolean }) =>
      req('POST', '/notifications/read', { body, schema: MarkReadResult, redirectOn401: false }),

    // web push (members, U52)
    getPushConfig: () =>
      req('GET', '/push/config', { schema: api.PushConfig, redirectOn401: false }),
    subscribePush: (body: api.PushSubscriptionBody) => req('POST', '/push/subscriptions', { body }),
    unsubscribePush: (endpoint: string) =>
      req('DELETE', '/push/subscriptions', { body: { endpoint } }),

    // change history (members, U50)
    getHistory: (boardId: string, before?: string) =>
      req('GET', `/boards/${enc(boardId)}/history`, {
        schema: api.HistoryPage,
        query: before === undefined ? { limit: 50 } : { limit: 50, before },
      }),

    // snapshots (members)
    listSnapshots: (boardId: string) =>
      req('GET', `/boards/${enc(boardId)}/snapshots`, { schema: listOf(api.Snapshot) }),
    createSnapshot: (boardId: string, name: string) =>
      req('POST', `/boards/${enc(boardId)}/snapshots`, { body: { name }, schema: api.Snapshot }),
    getSnapshot: (sid: string) =>
      req('GET', `/snapshots/${enc(sid)}`, { schema: api.SnapshotDetail }),
    restoreSnapshot: (sid: string) =>
      req('POST', `/snapshots/${enc(sid)}/restore`, { schema: api.DocumentResponse }),
    uploadSnapshotPdf: (sid: string, pdf: Blob) =>
      req('PUT', `/snapshots/${enc(sid)}/pdf`, {
        rawBody: pdf,
        headers: { 'Content-Type': 'application/pdf' },
        schema: api.Snapshot,
      }),
    // released versions (U80)
    releaseSnapshot: (sid: string) =>
      req('POST', `/snapshots/${enc(sid)}/release`, { schema: api.Snapshot }),
    signOff: (
      sid: string,
      body: { name: string; role: string; comment?: string; signature?: string },
    ) => req('POST', `/snapshots/${enc(sid)}/signoffs`, { body, schema: api.Snapshot }),
    /** Path of a drawn signature image (same-origin, sent with the session cookie). */
    signatureUrl: (signoffId: string) => client.url(`/signoffs/${enc(signoffId)}/signature`),
    getReleaseSettings: (boardId: string) =>
      req('GET', `/boards/${enc(boardId)}/release-settings`, { schema: api.ReleaseSettings }),
    setReleaseSettings: (boardId: string, requireSignature: boolean) =>
      req('PUT', `/boards/${enc(boardId)}/release-settings`, {
        body: { requireSignature },
        schema: api.ReleaseSettings,
      }),
    boardDocument: (boardId: string) =>
      req('GET', `/boards/${enc(boardId)}/document`, { schema: api.DocumentResponse }),

    // workspace members / settings
    getWorkspace: (ws: string) => req('GET', `/workspaces/${enc(ws)}`, { schema: api.Workspace }),
    renameWorkspace: (ws: string, name: string) =>
      req('PATCH', `/workspaces/${enc(ws)}`, { body: { name }, schema: api.Workspace }),
    listMembers: (ws: string) =>
      req('GET', `/workspaces/${enc(ws)}/members`, { schema: listOf(api.Member) }),
    addMember: (ws: string, body: { email: string; role: api.Member['role'] }) =>
      req('POST', `/workspaces/${enc(ws)}/members`, { body, schema: api.Member }),
    updateMember: (ws: string, userId: string, role: api.Member['role']) =>
      req('PATCH', `/workspaces/${enc(ws)}/members/${enc(userId)}`, {
        body: { role },
        schema: api.Member,
      }),
    removeMember: (ws: string, userId: string) =>
      req<void>('DELETE', `/workspaces/${enc(ws)}/members/${enc(userId)}`),

    // platform admin
    adminStats: () => req('GET', '/admin/stats', { schema: api.AdminStats }),
    lockUser: (userId: string, locked: boolean) =>
      req('POST', `/admin/users/${enc(userId)}/lock`, { body: { locked }, schema: LockResult }),

    // edit lease (edit guests use the same endpoint as members)
    acquireLease: (boardId: string, takeover = false) =>
      req('POST', `/boards/${enc(boardId)}/lease`, {
        body: takeover ? { takeover: true } : {},
        schema: api.LeaseResponse,
      }),
    releaseLease: (boardId: string, keepalive = false) =>
      req<void>('DELETE', `/boards/${enc(boardId)}/lease`, { keepalive, redirectOn401: false }),

    fileUrl: (fileId: string) => client.url(`/files/${enc(fileId)}`),
  };
}

export type SharingApi = ReturnType<typeof createSharingApi>;

/** Always built on the current app client (tests swap it with `setApi`). */
export function sharingApi(guest = false): SharingApi {
  return createSharingApi(getApi().client, { guest });
}

/** Presigned upload of a comment attachment (image or PDF): POST /boards/:id/files, then PUT. */
export async function uploadAttachment(
  s: SharingApi,
  boardId: string,
  file: File,
  fetchImpl: typeof fetch = (...a) => globalThis.fetch(...a),
): Promise<string> {
  if (!isAttachmentMime(file.type))
    throw new ApiError(415, 'unsupported_media_type', file.type, '');
  const { fileId, uploadUrl } = await s.createUpload(boardId, {
    name: file.name || 'file',
    mime: file.type,
    size: file.size,
  });
  let res: Response;
  try {
    res = await fetchImpl(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
  } catch (err) {
    throw new NetworkError(uploadUrl, err);
  }
  if (!res.ok) throw new ApiError(res.status, 'upload_failed', res.statusText, '');
  return fileId;
}
